import { useState } from 'react';
import { queryApplicationsByPhone, type ApplicationStatusItem } from '../../../api/dripPlan';

const STATUS_MAP: Record<string, string> = {
  pending: '待审核',
  approved: '审核通过',
  supplement: '需补充',
  rejected: '已拒绝',
  delivering: '准备配送',
};

function buildTimeline(item: ApplicationStatusItem): { step: string; completed: boolean; time: string }[] {
  const steps = [
    { step: '提交申请', completed: true, time: item.applyTime || '' },
    {
      step: '材料审核',
      completed: item.status !== 'pending',
      time: item.status !== 'pending' ? (item.updatedAt || '') : '',
    },
    {
      step: item.status === 'supplement' ? '需补充' : item.status === 'rejected' ? '已拒绝' : '审核结果',
      completed: ['approved', 'supplement', 'rejected', 'delivering'].includes(item.status),
      time: ['approved', 'supplement', 'rejected', 'delivering'].includes(item.status) ? (item.updatedAt || '') : '',
    },
    {
      step: '安排配送',
      completed: item.status === 'delivering',
      time: item.status === 'delivering' ? (item.updatedAt || '') : '',
    },
  ];
  return steps;
}

function getStatusColor(status: string) {
  switch (status) {
    case 'pending':
      return 'text-[#F4B942]';
    case 'approved':
    case 'delivering':
      return 'text-[#2A7A62]';
    case 'supplement':
    case 'rejected':
      return 'text-red-500';
    default:
      return 'text-[#6C6C6C]';
  }
}

function getStatusBgColor(status: string) {
  switch (status) {
    case 'pending':
      return 'bg-[#FFF9E6]';
    case 'approved':
    case 'delivering':
      return 'bg-[#E8F5F1]';
    case 'supplement':
    case 'rejected':
      return 'bg-red-50';
    default:
      return 'bg-[#F9F9F9]';
  }
}

const StatusQuery = () => {
  const [phone, setPhone] = useState('');
  const [list, setList] = useState<ApplicationStatusItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);

  const handleQuery = async () => {
    const trimmed = phone.trim();
    if (!trimmed) {
      setError('请输入手机号');
      return;
    }
    if (!/^1[3-9]\d{9}$/.test(trimmed)) {
      setError('手机号格式不正确');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const data = await queryApplicationsByPhone(trimmed);
      setList(data);
      setSelectedIndex(0);
    } catch (e) {
      setList([]);
      setError(e instanceof Error ? e.message : '查询失败');
    } finally {
      setLoading(false);
    }
  };

  const selected = list.length > 0 ? list[selectedIndex] : null;
  const showResult = list.length > 0 && selected;

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 lg:p-12">
      <div className="text-center mb-6 md:mb-8">
        <h2 className="text-2xl md:text-4xl font-bold text-[#1E1E1E] mb-2 md:mb-4">查询申请状态</h2>
        <p className="text-sm md:text-lg text-[#6C6C6C]">输入您的手机号，查看审核进度</p>
      </div>

      <div className="max-w-2xl mx-auto">
        <div className="flex flex-col md:flex-row gap-3 md:gap-4 mb-6 md:mb-8">
          <input
            type="tel"
            inputMode="numeric"
            maxLength={11}
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value.replace(/\D/g, ''));
              setError('');
            }}
            placeholder="请输入手机号，如：13800138000"
            className="flex-1 h-12 md:h-14 px-4 md:px-6 border-2 border-[#E0E0E0] rounded-xl focus:border-[#2A7A62] focus:outline-none transition-colors text-sm md:text-base"
          />
          <button
            onClick={handleQuery}
            disabled={loading}
            className="px-6 py-3 md:px-8 md:py-3 bg-gradient-to-r from-[#2A7A62] to-[#3A9A82] text-white rounded-xl font-medium hover:shadow-lg transition-all duration-300 cursor-pointer whitespace-nowrap text-sm md:text-base disabled:opacity-60"
          >
            <i className="ri-search-line mr-2"></i>
            {loading ? '查询中…' : '查询'}
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm">
            {error}
          </div>
        )}

        {showResult && selected && (
          <div className="animate-[fadeIn_0.3s_ease-out]">
            {list.length > 1 && (
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-sm text-[#6C6C6C]">共 {list.length} 条申请：</span>
                {list.map((item, i) => (
                  <button
                    key={item.id}
                    onClick={() => setSelectedIndex(i)}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                      selectedIndex === i
                        ? 'bg-[#2A7A62] text-white'
                        : 'bg-[#F0F0F0] text-[#1E1E1E] hover:bg-[#E0E0E0]'
                    }`}
                  >
                    {item.applicationNumber}
                  </button>
                ))}
              </div>
            )}

            <div className={`${getStatusBgColor(selected.status)} rounded-xl p-4 md:p-6 mb-4 md:mb-6`}>
              <div className="flex items-center justify-between mb-3 md:mb-4">
                <div>
                  <p className="text-xs md:text-sm text-[#6C6C6C] mb-1">申请编号</p>
                  <p className="text-base md:text-xl font-bold text-[#1E1E1E]">{selected.applicationNumber}</p>
                </div>
                <div className={`px-4 py-1.5 md:px-6 md:py-2 rounded-full border-2 ${getStatusColor(selected.status)} border-current ${getStatusBgColor(selected.status)}`}>
                  <span className={`text-sm md:text-lg font-bold ${getStatusColor(selected.status)}`}>
                    {STATUS_MAP[selected.status] || selected.status}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4 text-xs md:text-sm">
                <div>
                  <span className="text-[#6C6C6C]">申请人：</span>
                  <span className="text-[#1E1E1E] font-medium ml-2">{selected.name}</span>
                </div>
                <div>
                  <span className="text-[#6C6C6C]">提交时间：</span>
                  <span className="text-[#1E1E1E] font-medium ml-2">{selected.applyTime}</span>
                </div>
              </div>
              {selected.status === 'supplement' && selected.auditRemark && (
                <div className="mt-3 text-sm text-[#6C6C6C]">
                  <span className="text-[#6C6C6C]">补充说明：</span>
                  <span className="text-[#1E1E1E] ml-2">{selected.auditRemark}</span>
                </div>
              )}
            </div>

            <div className="bg-white border border-[#E0E0E0] rounded-xl p-4 md:p-6">
              <h3 className="text-base md:text-lg font-bold text-[#1E1E1E] mb-4 md:mb-6">审核进度</h3>
              <div className="space-y-3 md:space-y-4">
                {buildTimeline(selected).map((item, index) => (
                  <div key={index} className="flex items-start gap-3 md:gap-4">
                    <div
                      className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        item.completed ? 'bg-gradient-to-br from-[#2A7A62] to-[#3A9A82]' : 'bg-[#E0E0E0]'
                      }`}
                    >
                      {item.completed ? (
                        <i className="ri-checkbox-circle-fill text-white text-lg md:text-xl"></i>
                      ) : (
                        <i className="ri-checkbox-blank-circle-line text-white text-lg md:text-xl"></i>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className={`font-medium text-sm md:text-base ${item.completed ? 'text-[#1E1E1E]' : 'text-[#6C6C6C]'}`}>
                        {item.step}
                      </p>
                      {item.time && <p className="text-xs md:text-sm text-[#6C6C6C] mt-1">{item.time}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4 md:mt-6 bg-[#F9F9F9] rounded-xl p-3 md:p-4">
              <p className="text-xs md:text-sm text-[#6C6C6C]">
                <i className="ri-information-line text-[#2A7A62] mr-2"></i>
                如有疑问，请联系客服：023-12345678
              </p>
            </div>
          </div>
        )}

        {!showResult && !loading && (
          <div className="mt-8 md:mt-12">
            <h3 className="text-base md:text-lg font-bold text-[#1E1E1E] mb-4 md:mb-6 text-center">状态说明</h3>
            <div className="grid grid-cols-3 md:grid-cols-3 gap-3 md:gap-4">
              <div className="bg-[#FFF9E6] rounded-lg md:rounded-xl p-3 md:p-4 text-center">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-[#F4B942] rounded-full flex items-center justify-center mx-auto mb-2 md:mb-3">
                  <i className="ri-time-line text-white text-lg md:text-xl"></i>
                </div>
                <p className="font-bold text-[#1E1E1E] mb-1 text-xs md:text-base">待审核</p>
                <p className="text-xs md:text-sm text-[#6C6C6C]">申请已提交</p>
              </div>
              <div className="bg-[#E8F5F1] rounded-lg md:rounded-xl p-3 md:p-4 text-center">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-[#2A7A62] rounded-full flex items-center justify-center mx-auto mb-2 md:mb-3">
                  <i className="ri-checkbox-circle-line text-white text-lg md:text-xl"></i>
                </div>
                <p className="font-bold text-[#1E1E1E] mb-1 text-xs md:text-base">审核通过</p>
                <p className="text-xs md:text-sm text-[#6C6C6C]">准备配送</p>
              </div>
              <div className="bg-red-50 rounded-lg md:rounded-xl p-3 md:p-4 text-center">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-2 md:mb-3">
                  <i className="ri-close-circle-line text-white text-lg md:text-xl"></i>
                </div>
                <p className="font-bold text-[#1E1E1E] mb-1 text-xs md:text-base">需补充</p>
                <p className="text-xs md:text-sm text-[#6C6C6C]">补充材料</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatusQuery;
